/**
 * ============================================
 * REMOTE CONFIG EDITOR
 * ============================================
 *
 * Dual-mode (UI / JSON) editor for the remote game configuration.
 * Admin users can edit values inline; others see a read-only view.
 *
 * Sections:
 *   Urls              – key-value editing, add / delete entries
 *   ServerConfigMaps  – version management, server config table
 *   ServerDownTimeData – IST datetime pickers + C# ticks conversion
 *   (anything else)   – generic formatted JSON display
 *
 * C# DateTime ticks:
 *   ticks → UTC ms  = (ticks / 10000) − 62135596800000
 *   UTC ms → ticks  = (utcMs + 62135596800000) × 10000
 *   IST             = UTC + 5 h 30 m  (19 800 000 ms)
 */

import { Fragment, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  X, RefreshCw, Globe, Server, AlertTriangle, ExternalLink,
  Clock, Pencil, Check, XCircle, Plus, Trash2, RotateCcw,
  Save, Code, LayoutGrid, ShieldCheck, Eye,
  Link2, Layers, TimerOff, FileJson,
  ChevronLeft, ChevronRight, Calendar,
} from 'lucide-react';
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  addMonths, subMonths, eachDayOfInterval, isSameMonth,
  isSameDay, format,
} from 'date-fns';
import { BACKEND_URL } from '@/config';

// ─── Types ───────────────────────────────────────────────

interface RemoteConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  isAdmin: boolean;
  username: string;
}

type ConfigData = Record<string, unknown>;

interface SaveStatus { type: 'success' | 'error'; message: string }

interface RemoteConfigResponse {
  configured_url: string;
  raw_data: ConfigData;
  parsed_endpoints: Record<string, unknown[]>;
  monitoring_config: Record<string, unknown>;
}

// ─── C# Ticks ↔ IST helpers ─────────────────────────────
//
// The game stores IST wall-clock time directly as C# DateTime ticks.
// e.g. 18:00 IST → new DateTime(2026,2,17,18,0,0).Ticks
// No UTC conversion is applied — ticks encode the IST time as-is.
//
// C# ticks = 100-nanosecond intervals since 0001-01-01 00:00:00
// Epoch offset = 62,135,596,800,000 ms (0001-01-01 → 1970-01-01)

const EPOCH_OFFSET_MS = 62135596800000;

/** Convert C# ticks → JS milliseconds (epoch-based) */
function ticksToMs(ticks: number): number {
  if (!ticks) return 0;
  return Math.round(ticks / 10000) - EPOCH_OFFSET_MS;
}

/** Convert JS milliseconds → C# ticks */
function msToTicks(ms: number): number {
  if (!ms) return 0;
  return Math.round((ms + EPOCH_OFFSET_MS) * 10000);
}

/** Display ticks as a readable IST string */
function ticksToISTDisplay(ticks: number): string {
  if (!ticks) return 'Not set';
  const d = new Date(ticksToMs(ticks));
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${d.getUTCDate()} ${months[d.getUTCMonth()]} ${d.getUTCFullYear()}, ${String(d.getUTCHours()).padStart(2,'0')}:${String(d.getUTCMinutes()).padStart(2,'0')} IST`;
}

/** Convert ticks → YYYY-MM-DDTHH:mm for the DateTimePicker */
function ticksToDatetimeLocal(ticks: number): string {
  if (!ticks) return '';
  const d = new Date(ticksToMs(ticks));
  const y  = d.getUTCFullYear();
  const mo = String(d.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const h  = String(d.getUTCHours()).padStart(2, '0');
  const mi = String(d.getUTCMinutes()).padStart(2, '0');
  return `${y}-${mo}-${dd}T${h}:${mi}`;
}

/** Convert YYYY-MM-DDTHH:mm (IST) → C# ticks */
function datetimeLocalToTicks(val: string): number {
  if (!val) return 0;
  const [datePart, timePart] = val.split('T');
  const [y, m, d]  = datePart.split('-').map(Number);
  const [h, min]   = timePart.split(':').map(Number);
  const ms = Date.UTC(y, m - 1, d, h, min, 0, 0);
  return msToTicks(ms);
}

// ─── Helpers ─────────────────────────────────────────────

function deepClone<T>(obj: T): T { return JSON.parse(JSON.stringify(obj)); }

function sectionLabel(key: string): string {
  return key.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()).trim();
}

const SECTION_META: Record<string, { icon: React.ReactNode; color: string; description: string }> = {
  Urls:               { icon: <Link2 className="h-3.5 w-3.5" />,    color: 'text-blue-500 bg-blue-500/10 border-blue-500/20',    description: 'External links and endpoints' },
  ServerConfigMaps:   { icon: <Layers className="h-3.5 w-3.5" />,   color: 'text-violet-500 bg-violet-500/10 border-violet-500/20', description: 'Versioned server configurations' },
  ServerDownTimeData: { icon: <TimerOff className="h-3.5 w-3.5" />, color: 'text-orange-500 bg-orange-500/10 border-orange-500/20', description: 'Maintenance window scheduling' },
};
const DEFAULT_SECTION_META = { icon: <FileJson className="h-3.5 w-3.5" />, color: 'text-muted-foreground bg-muted/40 border-border/50', description: '' };

function SectionHeading({ sectionKey }: { sectionKey: string }) {
  const meta = SECTION_META[sectionKey] || DEFAULT_SECTION_META;
  return (
    <div className="flex items-center gap-2.5 mb-3">
      <span className={`inline-flex items-center justify-center h-7 w-7 rounded-lg border ${meta.color}`}>
        {meta.icon}
      </span>
      <div className="flex flex-col">
        <span className="text-sm font-semibold text-foreground leading-tight">{sectionLabel(sectionKey)}</span>
        {meta.description && (
          <span className="text-[10px] text-muted-foreground leading-tight">{meta.description}</span>
        )}
      </div>
    </div>
  );
}

// ─── InlineEdit ──────────────────────────────────────────

function InlineEdit({
  value, onSave, canEdit, type = 'text', className = '',
}: {
  value: string | number | boolean;
  onSave: (v: string | number | boolean) => void;
  canEdit: boolean;
  type?: 'text' | 'number' | 'boolean' | 'url';
  className?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(String(value));
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { if (editing) inputRef.current?.focus(); }, [editing]);

  const commit = () => {
    const parsed = type === 'number' ? (Number(draft) || 0) : draft;
    onSave(parsed);
    setEditing(false);
  };

  // Boolean: simple toggle
  if (type === 'boolean') {
    return (
      <span className="inline-flex items-center gap-1.5">
        <span className={`text-xs font-medium ${value ? 'text-green-500' : 'text-red-400'}`}>{String(value)}</span>
        {canEdit && (
          <button onClick={() => onSave(!value)} className="p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground" title="Toggle">
            <Pencil className="h-3 w-3" />
          </button>
        )}
      </span>
    );
  }

  // Display mode
  if (!editing) {
    const isUrl = type === 'url' && typeof value === 'string' && /^https?:\/\//.test(value);
    const display = isUrl
      ? <a href={value as string} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-1 break-all text-xs">{String(value)} <ExternalLink className="h-3 w-3 flex-shrink-0" /></a>
      : <span className={`text-xs break-all ${className}`}>{String(value)}</span>;
    return (
      <span className="inline-flex items-center gap-1.5 group">
        {display}
        {canEdit && (
          <button onClick={() => { setDraft(String(value)); setEditing(true); }} className="p-0.5 rounded hover:bg-muted text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" title="Edit">
            <Pencil className="h-3 w-3" />
          </button>
        )}
      </span>
    );
  }

  // Edit mode
  return (
    <span className="inline-flex items-center gap-1">
      <input
        ref={inputRef}
        type={type === 'number' ? 'number' : 'text'}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') setEditing(false); }}
        className="px-2 py-0.5 text-xs border border-primary/50 rounded bg-background focus:outline-none focus:ring-1 focus:ring-primary min-w-[100px]"
      />
      <button onClick={commit} className="p-0.5 text-green-500 hover:text-green-400" title="Confirm"><Check className="h-3.5 w-3.5" /></button>
      <button onClick={() => setEditing(false)} className="p-0.5 text-red-400 hover:text-red-300" title="Cancel"><XCircle className="h-3.5 w-3.5" /></button>
    </span>
  );
}

// ─── URLs Editor ─────────────────────────────────────────

function UrlsEditor({ data, onChange, isAdmin }: {
  data: Record<string, string>;
  onChange: (v: Record<string, string>) => void;
  isAdmin: boolean;
}) {
  const [addingNew, setAddingNew] = useState(false);
  const [newKey, setNewKey]       = useState('');
  const [newValue, setNewValue]   = useState('');

  const handleAdd = () => {
    if (!newKey.trim()) return;
    onChange({ ...data, [newKey.trim()]: newValue });
    setNewKey(''); setNewValue(''); setAddingNew(false);
  };

  return (
    <div className="space-y-1.5">
      {Object.entries(data).map(([key, url]) => (
        <div key={key} className="group flex flex-col sm:flex-row sm:items-center justify-between p-2.5 rounded-lg bg-muted/30 border border-border/50 gap-1 sm:gap-2">
          <span className="text-xs font-medium flex-shrink-0">{key}</span>
          <div className="flex items-center gap-1.5 min-w-0 overflow-hidden">
            <InlineEdit value={url} onSave={v => onChange({ ...data, [key]: String(v) })} canEdit={isAdmin} type="url" />
            {isAdmin && (
              <button onClick={() => { const u = { ...data }; delete u[key]; onChange(u); }} className="p-0.5 text-red-400 hover:text-red-300 opacity-0 group-hover:opacity-100 transition-opacity" title="Remove">
                <Trash2 className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>
      ))}

      {isAdmin && !addingNew && (
        <button onClick={() => setAddingNew(true)} className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 mt-2">
          <Plus className="h-3.5 w-3.5" /> Add URL
        </button>
      )}

      {addingNew && (
        <div className="flex items-center gap-2 p-2.5 rounded-lg border border-primary/30 bg-primary/5">
          <input placeholder="Key (e.g. SupportURL)" value={newKey} onChange={e => setNewKey(e.target.value)} className="px-2 py-1 text-xs border rounded bg-background flex-1 min-w-[80px]" autoFocus />
          <input placeholder="Value (URL)" value={newValue} onChange={e => setNewValue(e.target.value)} className="px-2 py-1 text-xs border rounded bg-background flex-[2]" onKeyDown={e => { if (e.key === 'Enter') handleAdd(); }} />
          <button onClick={handleAdd} className="p-1 text-green-500"><Check className="h-4 w-4" /></button>
          <button onClick={() => setAddingNew(false)} className="p-1 text-red-400"><XCircle className="h-4 w-4" /></button>
        </div>
      )}
    </div>
  );
}

// ─── ServerConfigMaps Editor ─────────────────────────────

interface ServerConfigMap {
  Version: string;
  ForceUpdate: boolean;
  ServerConfigs: Array<Record<string, unknown>>;
  [extra: string]: unknown;
}

function ServerConfigMapsEditor({ data, onChange, isAdmin }: {
  data: ServerConfigMap[];
  onChange: (v: ServerConfigMap[]) => void;
  isAdmin: boolean;
}) {
  const updateMap = (idx: number, field: string, value: unknown) => {
    const updated = deepClone(data);
    (updated[idx] as Record<string, unknown>)[field] = value;
    onChange(updated);
  };

  const updateSC = (mapIdx: number, scIdx: number, field: string, value: unknown) => {
    const updated = deepClone(data);
    updated[mapIdx].ServerConfigs[scIdx][field] = value;
    onChange(updated);
  };

  const addVersion = () => {
    const template: ServerConfigMap = data.length > 0
      ? deepClone(data[data.length - 1])
      : { Version: '1.0', ForceUpdate: false, ServerConfigs: [] };
    const parts = template.Version.split('.');
    parts[parts.length - 1] = String(Number(parts[parts.length - 1]) + 1);
    template.Version = parts.join('.');
    onChange([...data, template]);
  };

  return (
    <div className="space-y-3">
      {data.map((cm, mapIdx) => {
        const scFields = cm.ServerConfigs.length > 0
          ? Object.keys(cm.ServerConfigs[0]).filter(k => !['Host','Port','Scheme','ServerType'].includes(k))
          : [];

        return (
          <div key={mapIdx} className="rounded-lg border border-border overflow-hidden">
            {/* Header */}
            <div className={`flex items-center justify-between px-3 sm:px-4 py-2 gap-2 ${cm.ForceUpdate ? 'bg-yellow-500/10 border-b border-yellow-500/20' : 'bg-muted/30 border-b border-border'}`}>
              <div className="flex items-center gap-2 sm:gap-3 flex-wrap min-w-0">
                <span className="text-sm font-semibold inline-flex items-center gap-1">
                  v<InlineEdit value={cm.Version} onSave={v => updateMap(mapIdx, 'Version', String(v))} canEdit={isAdmin} />
                </span>
                <span className="flex items-center gap-1.5 text-xs">
                  Force:
                  <InlineEdit value={cm.ForceUpdate} onSave={v => updateMap(mapIdx, 'ForceUpdate', v)} canEdit={isAdmin} type="boolean" />
                </span>
                {cm.ForceUpdate && <AlertTriangle className="h-3.5 w-3.5 text-yellow-500" />}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{cm.ServerConfigs?.length || 0} endpoint{(cm.ServerConfigs?.length || 0) !== 1 ? 's' : ''}</span>
                {isAdmin && data.length > 1 && (
                  <button onClick={() => { const u = [...data]; u.splice(mapIdx, 1); onChange(u); }} className="p-1 text-red-400 hover:text-red-300" title="Remove version">
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Server configs — desktop table */}
            {cm.ServerConfigs.length > 0 && (
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left px-4 py-2 font-semibold text-muted-foreground">Type</th>
                      <th className="text-left px-4 py-2 font-semibold text-muted-foreground">Host</th>
                      <th className="text-left px-4 py-2 font-semibold text-muted-foreground">Port</th>
                      <th className="text-left px-4 py-2 font-semibold text-muted-foreground">Scheme</th>
                      {scFields.map(k => <th key={k} className="text-left px-4 py-2 font-semibold text-muted-foreground">{k}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {cm.ServerConfigs.map((sc, scIdx) => (
                      <tr key={scIdx} className="border-b border-border/50 last:border-0">
                        <td className="px-4 py-2">
                          <span className="inline-flex items-center gap-1"><Server className="h-3 w-3 text-muted-foreground" /><span className="font-medium">{String(sc.ServerType)}</span></span>
                        </td>
                        <td className="px-4 py-2"><InlineEdit value={String(sc.Host)} onSave={v => updateSC(mapIdx, scIdx, 'Host', v)} canEdit={isAdmin} /></td>
                        <td className="px-4 py-2"><InlineEdit value={Number(sc.Port)} onSave={v => updateSC(mapIdx, scIdx, 'Port', v)} canEdit={isAdmin} type="number" /></td>
                        <td className="px-4 py-2"><InlineEdit value={String(sc.Scheme)} onSave={v => updateSC(mapIdx, scIdx, 'Scheme', v)} canEdit={isAdmin} /></td>
                        {scFields.map(k => (
                          <td key={k} className="px-4 py-2">
                            <InlineEdit
                              value={sc[k] as string | number | boolean}
                              onSave={v => updateSC(mapIdx, scIdx, k, v)}
                              canEdit={isAdmin}
                              type={typeof sc[k] === 'number' ? 'number' : typeof sc[k] === 'boolean' ? 'boolean' : 'text'}
                            />
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Server configs — mobile cards */}
            {cm.ServerConfigs.length > 0 && (
              <div className="sm:hidden divide-y divide-border/50">
                {cm.ServerConfigs.map((sc, scIdx) => (
                  <div key={scIdx} className="p-3 space-y-1.5">
                    <div className="flex items-center gap-1.5 mb-2">
                      <Server className="h-3 w-3 text-muted-foreground" />
                      <span className="text-xs font-semibold">{String(sc.ServerType)}</span>
                    </div>
                    <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs">
                      <span className="text-muted-foreground">Host</span>
                      <span className="break-all"><InlineEdit value={String(sc.Host)} onSave={v => updateSC(mapIdx, scIdx, 'Host', v)} canEdit={isAdmin} /></span>
                      <span className="text-muted-foreground">Port</span>
                      <span><InlineEdit value={Number(sc.Port)} onSave={v => updateSC(mapIdx, scIdx, 'Port', v)} canEdit={isAdmin} type="number" /></span>
                      <span className="text-muted-foreground">Scheme</span>
                      <span><InlineEdit value={String(sc.Scheme)} onSave={v => updateSC(mapIdx, scIdx, 'Scheme', v)} canEdit={isAdmin} /></span>
                      {scFields.map(k => (
                        <Fragment key={k}>
                          <span className="text-muted-foreground">{k}</span>
                          <span>
                            <InlineEdit
                              value={sc[k] as string | number | boolean}
                              onSave={v => updateSC(mapIdx, scIdx, k, v)}
                              canEdit={isAdmin}
                              type={typeof sc[k] === 'number' ? 'number' : typeof sc[k] === 'boolean' ? 'boolean' : 'text'}
                            />
                          </span>
                        </Fragment>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}

      {isAdmin && (
        <button onClick={addVersion} className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80">
          <Plus className="h-3.5 w-3.5" /> Add Version
        </button>
      )}
    </div>
  );
}

// ─── DateTime Picker (Calendar + Time) ───────────────────

function DateTimePicker({ value, onChange }: {
  value: string;          // YYYY-MM-DDTHH:mm (IST)
  onChange: (v: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Parse current value or default to now
  const parsed = value ? new Date(value + ':00') : new Date();
  const [viewMonth, setViewMonth] = useState(parsed);
  const [selectedDate, setSelectedDate] = useState(parsed);
  const [hour, setHour] = useState(parsed.getHours());
  const [minute, setMinute] = useState(parsed.getMinutes());

  // Sync when value changes externally
  useEffect(() => {
    if (value) {
      const d = new Date(value + ':00');
      setSelectedDate(d);
      setViewMonth(d);
      setHour(d.getHours());
      setMinute(d.getMinutes());
    }
  }, [value]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Build calendar grid
  const monthStart = startOfMonth(viewMonth);
  const monthEnd = endOfMonth(viewMonth);
  const calStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const calEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
  const days = eachDayOfInterval({ start: calStart, end: calEnd });

  const commit = (date: Date, h: number, m: number) => {
    const y = date.getFullYear();
    const mo = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const hh = String(h).padStart(2, '0');
    const mm = String(m).padStart(2, '0');
    onChange(`${y}-${mo}-${d}T${hh}:${mm}`);
  };

  const selectDay = (day: Date) => {
    setSelectedDate(day);
    commit(day, hour, minute);
  };

  const changeHour = (h: number) => {
    setHour(h);
    commit(selectedDate, h, minute);
  };

  const changeMinute = (m: number) => {
    setMinute(m);
    commit(selectedDate, hour, m);
  };

  const clearValue = () => {
    onChange('');
    setOpen(false);
  };

  const displayText = value
    ? format(new Date(value + ':00'), 'dd MMM yyyy, HH:mm') + ' IST'
    : 'Pick date & time';

  return (
    <div className="relative" ref={ref}>
      {/* Trigger button */}
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-2 px-2.5 py-1.5 text-xs border rounded-lg bg-background hover:bg-muted/30 focus:ring-1 focus:ring-primary focus:outline-none transition-colors text-left"
      >
        <Calendar className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
        <span className={value ? 'text-foreground' : 'text-muted-foreground'}>{displayText}</span>
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 mt-1 left-0 w-[280px] bg-popover border border-border rounded-xl shadow-xl p-3 space-y-3">
          {/* Month navigation */}
          <div className="flex items-center justify-between">
            <button onClick={() => setViewMonth(subMonths(viewMonth, 1))} className="p-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground">
              <ChevronLeft className="h-4 w-4" />
            </button>
            <span className="text-sm font-semibold">{format(viewMonth, 'MMMM yyyy')}</span>
            <button onClick={() => setViewMonth(addMonths(viewMonth, 1))} className="p-1 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground">
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>

          {/* Day headers */}
          <div className="grid grid-cols-7 text-center">
            {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => (
              <span key={d} className="text-[10px] font-medium text-muted-foreground py-1">{d}</span>
            ))}
          </div>

          {/* Day grid */}
          <div className="grid grid-cols-7 gap-0.5">
            {days.map((day, i) => {
              const inMonth = isSameMonth(day, viewMonth);
              const isSelected = isSameDay(day, selectedDate);
              const isToday = isSameDay(day, new Date());
              return (
                <button
                  key={i}
                  onClick={() => selectDay(day)}
                  className={`h-8 w-8 mx-auto rounded-lg text-xs font-medium transition-colors ${
                    isSelected
                      ? 'bg-primary text-primary-foreground'
                      : isToday
                        ? 'bg-primary/15 text-primary font-semibold'
                        : inMonth
                          ? 'text-foreground hover:bg-muted'
                          : 'text-muted-foreground/40'
                  }`}
                >
                  {day.getDate()}
                </button>
              );
            })}
          </div>

          {/* Time picker */}
          <div className="flex items-center gap-2 pt-2 border-t border-border">
            <Clock className="h-3.5 w-3.5 text-muted-foreground" />
            <select
              value={hour}
              onChange={e => changeHour(Number(e.target.value))}
              className="px-2 py-1 text-xs border rounded-md bg-background focus:ring-1 focus:ring-primary focus:outline-none appearance-none text-center w-14"
            >
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={i}>{String(i).padStart(2, '0')}</option>
              ))}
            </select>
            <span className="text-sm font-bold text-muted-foreground">:</span>
            <select
              value={minute}
              onChange={e => changeMinute(Number(e.target.value))}
              className="px-2 py-1 text-xs border rounded-md bg-background focus:ring-1 focus:ring-primary focus:outline-none appearance-none text-center w-14"
            >
              {Array.from({ length: 60 }, (_, i) => (
                <option key={i} value={i}>{String(i).padStart(2, '0')}</option>
              ))}
            </select>
            <span className="text-[10px] text-muted-foreground font-medium ml-1">IST</span>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between pt-1">
            <button onClick={clearValue} className="text-[10px] text-red-400 hover:text-red-300 font-medium">Clear</button>
            <button onClick={() => setOpen(false)} className="text-[10px] px-3 py-1 rounded-md bg-primary text-primary-foreground font-medium hover:bg-primary/90">Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Duration Picker (H:M:S → seconds) ──────────────────

function DurationPicker({ totalSeconds, onChange }: {
  totalSeconds: number;
  onChange: (seconds: number) => void;
}) {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;

  const update = (newH: number, newM: number, newS: number) => {
    onChange(newH * 3600 + newM * 60 + newS);
  };

  const selCls = "px-1.5 py-1.5 text-xs border rounded-md bg-background focus:ring-1 focus:ring-primary focus:outline-none appearance-none text-center w-[52px]";

  return (
    <div className="flex items-center gap-1">
      <div className="flex flex-col items-center">
        <select value={h} onChange={e => update(Number(e.target.value), m, s)} className={selCls}>
          {Array.from({ length: 100 }, (_, i) => (
            <option key={i} value={i}>{String(i).padStart(2, '0')}</option>
          ))}
        </select>
        <span className="text-[9px] text-muted-foreground mt-0.5">hr</span>
      </div>
      <span className="text-sm font-bold text-muted-foreground pb-3">:</span>
      <div className="flex flex-col items-center">
        <select value={m} onChange={e => update(h, Number(e.target.value), s)} className={selCls}>
          {Array.from({ length: 60 }, (_, i) => (
            <option key={i} value={i}>{String(i).padStart(2, '0')}</option>
          ))}
        </select>
        <span className="text-[9px] text-muted-foreground mt-0.5">min</span>
      </div>
      <span className="text-sm font-bold text-muted-foreground pb-3">:</span>
      <div className="flex flex-col items-center">
        <select value={s} onChange={e => update(h, m, Number(e.target.value))} className={selCls}>
          {Array.from({ length: 60 }, (_, i) => (
            <option key={i} value={i}>{String(i).padStart(2, '0')}</option>
          ))}
        </select>
        <span className="text-[9px] text-muted-foreground mt-0.5">sec</span>
      </div>
    </div>
  );
}

// ─── Downtime Editor ─────────────────────────────────────

function DowntimeEditor({ data, onChange, isAdmin }: {
  data: Record<string, number>;
  onChange: (v: Record<string, number>) => void;
  isAdmin: boolean;
}) {
  const startTicks = data.StartTimeUtcTicks || 0;
  const duration   = data.DurationInSeconds || 0;
  const countdown  = data.CountdownDurationInSeconds || 0;
  const isActive   = startTicks > 0 || duration > 0;

  const extraKeys = Object.keys(data).filter(
    k => !['StartTimeUtcTicks', 'DurationInSeconds', 'CountdownDurationInSeconds'].includes(k),
  );

  return (
    <div className={`p-4 rounded-lg border ${isActive ? 'bg-red-500/10 border-red-500/30' : 'bg-muted/30 border-border/50'}`}>
      <div className="flex items-center gap-2 mb-3">
        <Clock className="h-4 w-4 text-muted-foreground" />
        <span className={`text-xs font-semibold ${isActive ? 'text-red-400' : 'text-green-400'}`}>
          {isActive ? 'DOWNTIME CONFIGURED' : 'No downtime scheduled'}
        </span>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {/* Start Time */}
        <div>
          <label className="text-xs text-muted-foreground block mb-1">Start Time (IST)</label>
          {isAdmin ? (
            <DateTimePicker
              value={ticksToDatetimeLocal(startTicks)}
              onChange={v => onChange({ ...data, StartTimeUtcTicks: datetimeLocalToTicks(v) })}
            />
          ) : (
            <span className="text-xs font-medium">{ticksToISTDisplay(startTicks)}</span>
          )}
          <span className="text-[10px] text-muted-foreground block mt-0.5">Ticks: {startTicks.toLocaleString()}</span>
        </div>

        {/* Duration */}
        <div>
          <label className="text-xs text-muted-foreground block mb-1.5">Duration</label>
          {isAdmin ? (
            <DurationPicker totalSeconds={duration} onChange={v => onChange({ ...data, DurationInSeconds: v })} />
          ) : (
            <span className="text-xs font-medium">{Math.floor(duration / 3600)}h {Math.floor((duration % 3600) / 60)}m {duration % 60}s</span>
          )}
          <span className="text-[10px] text-muted-foreground block mt-1">= {duration.toLocaleString()} seconds</span>
        </div>

        {/* Countdown */}
        <div>
          <label className="text-xs text-muted-foreground block mb-1.5">Countdown</label>
          {isAdmin ? (
            <DurationPicker totalSeconds={countdown} onChange={v => onChange({ ...data, CountdownDurationInSeconds: v })} />
          ) : (
            <span className="text-xs font-medium">{Math.floor(countdown / 3600)}h {Math.floor((countdown % 3600) / 60)}m {countdown % 60}s</span>
          )}
          <span className="text-[10px] text-muted-foreground block mt-1">= {countdown.toLocaleString()} seconds</span>
        </div>
      </div>

      {/* Any extra fields */}
      {extraKeys.length > 0 && (
        <div className="mt-3 space-y-1">
          {extraKeys.map(key => (
            <div key={key} className="text-xs">
              <span className="text-muted-foreground">{key}: </span>
              <InlineEdit value={data[key]} onSave={v => onChange({ ...data, [key]: Number(v) })} canEdit={isAdmin} type="number" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Generic JSON (read-only fallback) ───────────────────

function GenericJsonSection({ data }: { data: unknown }) {
  return (
    <pre className="text-xs p-3 rounded-lg bg-muted/30 border border-border/50 overflow-x-auto whitespace-pre-wrap break-all">
      {JSON.stringify(data, null, 2)}
    </pre>
  );
}

// ─── Main Component ──────────────────────────────────────

export function RemoteConfigModal({ isOpen, onClose, isAdmin, username }: RemoteConfigModalProps) {
  const [configUrl, setConfigUrl]           = useState('');
  const [originalConfig, setOriginalConfig] = useState<ConfigData | null>(null);
  const [editedConfig, setEditedConfig]     = useState<ConfigData | null>(null);

  const [viewMode, setViewMode] = useState<'ui' | 'json'>('ui');
  const [jsonText, setJsonText] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving]   = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError]         = useState<string | null>(null);
  const [saveStatus, setSaveStatus] = useState<SaveStatus | null>(null);

  const hasChanges = useMemo(() => {
    if (!originalConfig || !editedConfig) return false;
    return JSON.stringify(originalConfig) !== JSON.stringify(editedConfig);
  }, [originalConfig, editedConfig]);

  // ── Fetch ──
  const fetchConfig = useCallback(async () => {
    setIsLoading(true); setError(null); setSaveStatus(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/v1/metrics/remote-config/debug`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const result: RemoteConfigResponse = await res.json();
      setConfigUrl(result.configured_url || '');
      setOriginalConfig(result.raw_data);
      setEditedConfig(deepClone(result.raw_data));
      setJsonText(JSON.stringify(result.raw_data, null, 2));
      setJsonError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { if (isOpen) fetchConfig(); }, [isOpen, fetchConfig]);

  // ── Mode switching ──
  const switchToUI = () => {
    if (viewMode !== 'json') return;
    try {
      setEditedConfig(JSON.parse(jsonText));
      setJsonError(null);
      setViewMode('ui');
    } catch (e) {
      setJsonError(e instanceof Error ? e.message : 'Invalid JSON');
    }
  };

  const switchToJSON = () => {
    if (viewMode !== 'ui') return;
    setJsonText(JSON.stringify(editedConfig, null, 2));
    setViewMode('json');
  };

  // ── Reset ──
  const handleReset = () => {
    if (!originalConfig) return;
    setEditedConfig(deepClone(originalConfig));
    setJsonText(JSON.stringify(originalConfig, null, 2));
    setJsonError(null);
    setSaveStatus(null);
  };

  // ── Save (with confirmation) ──
  const requestSave = () => {
    if (!editedConfig) return;
    // If in JSON mode, validate before showing confirm dialog
    if (viewMode === 'json') {
      try {
        JSON.parse(jsonText);
        setJsonError(null);
      } catch (e) {
        setJsonError(e instanceof Error ? e.message : 'Invalid JSON');
        return;
      }
    }
    setShowConfirm(true);
  };

  const handleSave = async () => {
    setShowConfirm(false);
    if (!editedConfig) return;
    let configToSave = editedConfig;
    if (viewMode === 'json') {
      try {
        configToSave = JSON.parse(jsonText);
        setEditedConfig(configToSave);
        setJsonError(null);
      } catch (e) {
        setJsonError(e instanceof Error ? e.message : 'Invalid JSON');
        return;
      }
    }
    setIsSaving(true); setSaveStatus(null);
    try {
      const res = await fetch(`${BACKEND_URL}/api/v1/metrics/remote-config/save`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, config: configToSave }),
      });
      const result = await res.json();
      if (res.ok && result.success) {
        setSaveStatus({ type: 'success', message: result.message || 'Saved!' });
        setOriginalConfig(deepClone(configToSave));
      } else {
        setSaveStatus({ type: 'error', message: result.error || result.message || 'Save failed' });
      }
    } catch (err) {
      setSaveStatus({ type: 'error', message: err instanceof Error ? err.message : 'Network error' });
    } finally {
      setIsSaving(false);
    }
  };

  // ── Section updater ──
  const updateSection = (key: string, value: unknown) => {
    setEditedConfig(prev => prev ? { ...prev, [key]: value } : prev);
  };

  if (!isOpen) return null;

  const configKeys = editedConfig ? Object.keys(editedConfig) : [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="relative w-full max-w-4xl max-h-[100vh] sm:max-h-[90vh] mx-0 sm:mx-4 bg-background border-0 sm:border border-border rounded-none sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col">
        {/* ── Header ── */}
        <div className="flex items-center justify-between p-3 sm:p-4 border-b border-border gap-2">
          <div className="flex items-center gap-2 sm:gap-3 min-w-0">
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground sm:hidden flex-shrink-0" title="Close">
              <X className="h-5 w-5" />
            </button>
            <Globe className="h-5 w-5 text-primary flex-shrink-0 hidden sm:block" />
            <h2 className="text-base sm:text-lg font-semibold truncate">Remote Config</h2>
            {isAdmin ? (
              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-green-500/15 text-green-500 border border-green-500/30 font-medium flex-shrink-0">
                <ShieldCheck className="h-3 w-3" /> Admin
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground border border-border font-medium flex-shrink-0">
                <Eye className="h-3 w-3" /> Read Only
              </span>
            )}
          </div>

          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            {/* View-mode toggle */}
            <div className="flex items-center bg-muted/50 rounded-lg p-0.5">
              <button
                onClick={switchToUI}
                className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 rounded-md text-xs font-medium transition-colors ${viewMode === 'ui' ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
              >
                <LayoutGrid className="h-3.5 w-3.5" /> <span className="hidden sm:inline">UI</span>
              </button>
              <button
                onClick={switchToJSON}
                className={`flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 rounded-md text-xs font-medium transition-colors ${viewMode === 'json' ? 'bg-background shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
              >
                <Code className="h-3.5 w-3.5" /> <span className="hidden sm:inline">JSON</span>
              </button>
            </div>

            <button onClick={fetchConfig} disabled={isLoading} className="p-1.5 sm:p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground" title="Refresh">
              <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground hidden sm:block">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* ── Content ── */}
        <div className="overflow-y-auto overflow-x-hidden flex-1 p-3 sm:p-4">
          {isLoading && !editedConfig && (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              <span className="ml-3 text-muted-foreground">Loading...</span>
            </div>
          )}

          {error && <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 text-red-500 text-sm">{error}</div>}

          {jsonError && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-xs mb-4">
              JSON Error: {jsonError}
            </div>
          )}

          {/* UI Mode */}
          {editedConfig && viewMode === 'ui' && (
            <div className="space-y-6">
              <div className="flex items-center gap-2.5 p-3 rounded-xl bg-muted/30 border border-border/50">
                <span className="inline-flex items-center justify-center h-7 w-7 rounded-lg border text-emerald-500 bg-emerald-500/10 border-emerald-500/20">
                  <Globe className="h-3.5 w-3.5" />
                </span>
                <div className="flex flex-col min-w-0">
                  <span className="text-[10px] text-muted-foreground font-medium uppercase tracking-wider">Source URL</span>
                  <code className="text-xs text-foreground break-all leading-snug">{configUrl || 'Not configured'}</code>
                </div>
              </div>

              {configKeys.map(key => (
                <div key={key}>
                  <SectionHeading sectionKey={key} />

                  {key === 'Urls' ? (
                    <UrlsEditor data={editedConfig[key] as Record<string, string>} onChange={v => updateSection(key, v)} isAdmin={isAdmin} />
                  ) : key === 'ServerConfigMaps' ? (
                    <ServerConfigMapsEditor data={editedConfig[key] as ServerConfigMap[]} onChange={v => updateSection(key, v)} isAdmin={isAdmin} />
                  ) : key === 'ServerDownTimeData' ? (
                    <DowntimeEditor data={editedConfig[key] as Record<string, number>} onChange={v => updateSection(key, v)} isAdmin={isAdmin} />
                  ) : (
                    <GenericJsonSection data={editedConfig[key]} />
                  )}
                </div>
              ))}
            </div>
          )}

          {/* JSON Mode */}
          {editedConfig && viewMode === 'json' && (
            <textarea
              value={jsonText}
              onChange={e => { setJsonText(e.target.value); setJsonError(null); }}
              readOnly={!isAdmin}
              className={`w-full h-[60vh] p-4 text-xs font-mono rounded-lg border bg-muted/30 focus:outline-none focus:ring-1 focus:ring-primary resize-none ${isAdmin ? 'border-border' : 'border-border/50 cursor-default'}`}
              spellCheck={false}
            />
          )}
        </div>

        {/* ── Footer (admin only) ── */}
        {isAdmin && editedConfig && (
          <div className="border-t border-border px-3 sm:px-4 py-3 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2">
            <div className="text-center sm:text-left">
              {saveStatus && (
                <span className={`text-xs ${saveStatus.type === 'success' ? 'text-green-500' : 'text-red-400'}`}>
                  {saveStatus.message}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2 justify-end">
              <button onClick={handleReset} disabled={!hasChanges || isSaving} className="flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed">
                <RotateCcw className="h-3.5 w-3.5" /> Reset
              </button>
              <button onClick={requestSave} disabled={!hasChanges || isSaving} className="flex items-center gap-1.5 px-4 py-1.5 text-xs rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed">
                {isSaving ? <RefreshCw className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
                Save Changes
              </button>
            </div>
          </div>
        )}

        {/* ── Confirmation Dialog ── */}
        {showConfirm && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-black/50 backdrop-blur-sm rounded-2xl">
            <div className="bg-background border border-border rounded-xl shadow-2xl p-6 max-w-sm mx-4 space-y-4">
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-yellow-500/15 border border-yellow-500/30">
                  <AlertTriangle className="h-5 w-5 text-yellow-500" />
                </span>
                <h3 className="text-sm font-semibold">Confirm Save to Production</h3>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                This will update the <strong>live remote config file</strong> that every game client reads.
                The change takes effect immediately.
              </p>
              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  onClick={() => setShowConfirm(false)}
                  className="px-4 py-1.5 text-xs rounded-lg border border-border hover:bg-muted"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="flex items-center gap-1.5 px-4 py-1.5 text-xs rounded-lg bg-red-600 text-white hover:bg-red-500"
                >
                  <Save className="h-3.5 w-3.5" /> Confirm Save
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
